import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Users, ChefHat, Flame, ChevronRight, Loader2, Star, Printer, Share2, Camera, ImagePlus, Heart, Bookmark, CookingPot } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { CookMode } from "@/components/CookMode";
import { MEDIA } from "@/lib/media";

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cookOpen, setCookOpen] = useState(false);

  useEffect(() => { api.recipe(id).then(setR).catch(() => setErr(true)); }, [id]);
  useEffect(() => {
    if (!isAuthed) { setSaved(false); return; }
    api.savedRecipeIds().then((d) => setSaved((d.ids || []).includes(id))).catch(() => {});
  }, [id, isAuthed]);

  const toggleSave = async () => {
    if (!isAuthed) { openAuth("login"); return; }
    try {
      const res = await api.saveRecipe(id);
      setSaved(res.saved);
      toast.success(res.saved ? "Saved to your recipes" : "Removed from saved");
    } catch { toast.error("Couldn't update your saved recipes."); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: r?.name || "HomeRange Recipe", text: r?.blurb || "A wild-game recipe from HomeRange", url });
      } catch { /* user cancelled the share sheet — do nothing */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Recipe link copied — take it to camp.");
    } catch {
      toast.error("Couldn't copy the link. Please copy it from the address bar.");
    }
  };

  if (err) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="recipe-notfound">
        <p className="font-serif text-3xl text-forest">Recipe not found.</p>
        <Link to="/recipes" className="text-blaze font-semibold mt-4 inline-block">Back to Recipes</Link>
      </div><Footer />
    </div>
  );
  if (!r) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  const meta = [
    r.time_minutes && { icon: Clock, label: `${r.time_minutes} min` },
    r.servings && { icon: Users, label: `Serves ${r.servings}` },
    r.difficulty && { icon: Flame, label: r.difficulty },
    r.avg_rating ? { icon: Star, label: `${r.avg_rating.toFixed(1)} (${r.rating_count || 0})` } : null,
  ].filter(Boolean);

  return (
    <div className="bg-paper min-h-screen" data-testid="recipe-detail">
      <SiteHeader />
      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden grain" data-testid="recipe-hero">
        <div className="absolute inset-0 z-0">
          <img src={r.image_url || MEDIA.exploreRecipes} alt={r.name} className="w-full h-full object-cover object-center opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1100px] mx-auto px-5 sm:px-10 pt-8 pb-14">
          <nav className="flex items-center gap-1.5 text-xs text-bone/60 mb-6" aria-label="Breadcrumb" data-no-print>
            <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} />
            <Link to="/recipes" className="hover:text-blaze">Recipes &amp; Films</Link><ChevronRight size={13} />
            <span className="text-bone font-medium">{r.name}</span>
          </nav>
          <span className="hr-overline text-blaze">{[r.game, r.cut].filter(Boolean).join(" · ")}</span>
          <h1 className="font-serif text-4xl sm:text-6xl leading-[0.92] tracking-tight mt-3 max-w-2xl">{r.name}</h1>
          {r.blurb && <p className="text-lg text-bone/80 mt-4 max-w-xl">{r.blurb}</p>}
          <div className="flex flex-wrap gap-5 mt-6">
            {meta.map((m) => { const Icon = m.icon; return (
              <span key={m.label} className="inline-flex items-center gap-1.5 text-sm text-bone/85" data-testid="recipe-meta"><Icon size={15} className="text-blaze" /> {m.label}</span>
            ); })}
          </div>
          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-7" data-no-print>
            <button onClick={() => setCookOpen(true)} className="inline-flex items-center gap-2 bg-blaze text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="recipe-cook-mode-btn">
              <CookingPot size={16} /> Cook mode
            </button>
            <button onClick={toggleSave} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-colors ${saved ? "bg-bone text-forest border-bone" : "bg-bone/10 border-bone/25 text-bone hover:bg-bone/20"}`} data-testid="recipe-save-btn">
              <Bookmark size={16} className={saved ? "fill-forest" : ""} /> {saved ? "Saved" : "Save"}
            </button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-bone/10 border border-bone/25 text-bone px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-bone/20 transition-colors" data-testid="recipe-print-btn">
              <Printer size={16} /> Print
            </button>
            <button onClick={handleShare} className="inline-flex items-center gap-2 bg-bone/10 border border-bone/25 text-bone px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-bone/20 transition-colors" data-testid="recipe-share-btn">
              <Share2 size={16} /> Share
            </button>
          </div>
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-10 py-12 grid lg:grid-cols-[0.9fr_1.4fr] gap-10 items-start">
        {/* Ingredients */}
        <aside className="bg-white border border-saddle/20 p-6 lg:sticky lg:top-24" data-testid="recipe-ingredients">
          <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><ChefHat size={20} className="text-blaze" /> Ingredients</h2>
          {(r.ingredients || []).length ? (
            <ul className="mt-4 space-y-2.5">
              {r.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-charcoal/80 border-b border-saddle/10 pb-2.5 last:border-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-blaze mt-1.5 shrink-0" />{ing}
                </li>
              ))}
            </ul>
          ) : <p className="text-charcoal/55 text-sm mt-3">Ingredient list coming soon.</p>}
          {(r.equipment || []).length > 0 && (
            <div className="mt-6 pt-5 border-t border-saddle/15" data-testid="recipe-equipment">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-saddle">Equipment</h3>
              <ul className="mt-3 space-y-2">
                {r.equipment.map((eq, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-charcoal/80"><span className="w-1.5 h-1.5 rounded-full bg-forest mt-1.5 shrink-0" />{eq}</li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Steps */}
        <section data-testid="recipe-steps">
          <h2 className="font-serif text-3xl text-forest">Method</h2>
          {(r.steps || []).length ? (
            <ol className="mt-5 space-y-5">
              {r.steps.map((step, i) => (
                <li key={i} className="flex gap-4" data-testid={`recipe-step-${i}`}>
                  <span className="shrink-0 w-9 h-9 rounded-full bg-forest text-bone font-serif text-lg grid place-items-center">{i + 1}</span>
                  <p className="text-charcoal/80 pt-1.5 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          ) : <p className="text-charcoal/55 mt-4">Steps coming soon.</p>}

          {r.notes && (
            <div className="mt-8 bg-bone border-l-2 border-blaze p-5" data-testid="recipe-notes">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-blaze">Notes &amp; tips</h3>
              <p className="text-charcoal/80 mt-2 leading-relaxed whitespace-pre-wrap">{r.notes}</p>
            </div>
          )}

          {(r.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-saddle/15">
              {r.tags.map((t) => <span key={t} className="text-[11px] uppercase tracking-wide text-charcoal/55 border border-saddle/25 px-2 py-1">{t}</span>)}
            </div>
          )}

          <button onClick={() => navigate("/recipes")} className="mt-8 inline-flex items-center gap-2 text-sm text-saddle hover:text-forest" data-no-print data-testid="recipe-back"><ArrowLeft size={15} /> All recipes</button>
        </section>
      </main>

      {/* Ratings + Finished plates — hidden from print */}
      <div data-no-print>
        {(r.related || []).length > 0 && (
          <section className="bg-bone border-t border-saddle/15" data-testid="recipe-related">
            <div className="max-w-[1100px] mx-auto px-5 sm:px-10 py-12">
              <h2 className="font-serif text-3xl text-forest">More to cook</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-6">
                {r.related.map((rel) => (
                  <button key={rel.id} onClick={() => navigate(`/recipes/${rel.id}`)} className="group relative h-[220px] overflow-hidden bg-charcoal flex flex-col justify-end text-left rounded-sm" data-testid={`related-recipe-${rel.id}`}>
                    <img src={rel.image_url || MEDIA.exploreRecipes} alt={rel.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="relative p-4">
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-blaze">{[rel.game, rel.cut].filter(Boolean).join(" · ")}</span>
                      <h3 className="font-serif text-xl text-bone leading-tight mt-1">{rel.name}</h3>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}
        <RatingsPanel recipeId={id} />
        <PhotoGallery recipeId={id} recipeName={r.name} />
      </div>

      <CookMode recipe={r} open={cookOpen} onClose={() => setCookOpen(false)} />
      <Footer />
    </div>
  );
}

/* ---------------- Ratings ---------------- */

function Stars({ value, size = 16, onSelect, hover, setHover }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover ?? value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={!onSelect}
            onClick={() => onSelect?.(n)}
            onMouseEnter={() => setHover?.(n)}
            onMouseLeave={() => setHover?.(0)}
            className={onSelect ? "cursor-pointer" : "cursor-default"}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={onSelect ? `rating-star-${n}` : undefined}
          >
            <Star size={size} className={active ? "fill-blaze text-blaze" : "text-saddle/40"} />
          </button>
        );
      })}
    </span>
  );
}

function RatingsPanel({ recipeId }) {
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [data, setData] = useState(null);
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.recipeRatings(recipeId).then(setData).catch(() => setData({ average: null, count: 0, reviews: [] }));
  useEffect(() => { load(); }, [recipeId]);

  const submit = async () => {
    if (!score) { toast.error("Pick a star rating first."); return; }
    setSaving(true);
    try {
      const res = await api.rateRecipe(recipeId, score, review.trim() || null);
      setData(res);
      setReview("");
      toast.success("Thanks — your rating is in.");
    } catch {
      toast.error("Couldn't save your rating. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white border-t border-saddle/15" data-testid="recipe-ratings-panel">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h2 className="font-serif text-3xl text-forest">Hunter ratings</h2>
          {data && data.count > 0 && (
            <div className="flex items-center gap-3" data-testid="recipe-rating-summary">
              <span className="font-serif text-4xl text-blaze leading-none">{data.average?.toFixed(1)}</span>
              <div>
                <Stars value={Math.round(data.average || 0)} />
                <p className="text-xs text-charcoal/55 mt-1">{data.count} rating{data.count > 1 ? "s" : ""}</p>
              </div>
            </div>
          )}
        </div>

        {/* Rating form */}
        <div className="mt-6 bg-paper border border-saddle/20 p-6 max-w-xl">
          {isAuthed ? (
            <>
              <p className="text-sm font-semibold text-forest">Rate this recipe</p>
              <div className="mt-3"><Stars value={score} size={26} onSelect={setScore} hover={hover} setHover={setHover} /></div>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Share how it turned out (optional)"
                rows={3}
                className="mt-4 w-full border border-saddle/25 bg-white px-3 py-2 text-sm text-charcoal focus:outline-none focus:border-blaze resize-none"
                data-testid="rating-review-input"
              />
              <button onClick={submit} disabled={saving} className="mt-3 inline-flex items-center gap-2 bg-forest text-bone px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-forest/90 transition-colors disabled:opacity-60" data-testid="rating-submit-btn">
                {saving && <Loader2 size={15} className="animate-spin" />} Submit rating
              </button>
            </>
          ) : (
            <div className="text-center py-2" data-testid="rating-signin-cta">
              <p className="text-sm text-charcoal/70">Sign in to rate this recipe and share how it cooked.</p>
              <button onClick={() => openAuth("login")} className="mt-3 inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="rating-signin-btn">Sign in to rate</button>
            </div>
          )}
        </div>

        {/* Reviews */}
        {data && data.reviews?.length > 0 && (
          <ul className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="recipe-reviews">
            {data.reviews.map((rv, i) => (
              <li key={i} className="bg-paper border border-saddle/15 p-5" data-testid={`recipe-review-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-forest">{rv.author_name}</span>
                  <Stars value={rv.rating} size={13} />
                </div>
                <p className="text-sm text-charcoal/75 mt-2 leading-relaxed">{rv.review}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ---------------- Finished-plate photos ---------------- */

async function fileToCompressedDataUrl(file, maxDim = 1400, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

function PhotoGallery({ recipeId }) {
  const { isAuthed, user } = useAuth();
  const { openAuth } = useUI();
  const [photos, setPhotos] = useState([]);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.recipePhotos(recipeId).then(setPhotos).catch(() => setPhotos([]));
  useEffect(() => { load(); }, [recipeId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("That image is too large (max 25 MB)."); return; }
    try {
      const compressed = await fileToCompressedDataUrl(file);
      setPreview(compressed);
    } catch {
      toast.error("Couldn't read that image.");
    }
  };

  const submit = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      await api.addRecipePhoto(recipeId, { photo: preview, caption: caption.trim() || null });
      setPreview(null);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      toast.success("Your finished plate is up. Looks good!");
    } catch {
      toast.error("Couldn't post your photo. Please try again.");
    } finally { setUploading(false); }
  };

  const toggleLike = async (photoId) => {
    if (!isAuthed) { openAuth("login"); return; }
    try {
      const updated = await api.likePhoto(photoId);
      setPhotos((prev) => prev.map((p) => (p.id === photoId ? updated : p)));
    } catch { /* no-op */ }
  };

  return (
    <section className="bg-paper border-t border-saddle/15" data-testid="recipe-photos-panel">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-10 py-12">
        <div className="flex items-center gap-2">
          <Camera size={22} className="text-blaze" />
          <h2 className="font-serif text-3xl text-forest">Finished plates</h2>
        </div>
        <p className="text-sm text-charcoal/60 mt-2">Cooked it? Show the table what you made.</p>

        {/* Upload */}
        <div className="mt-6 max-w-xl">
          {isAuthed ? (
            <div className="bg-white border border-saddle/20 p-6">
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="photo-file-input" />
              {!preview ? (
                <button onClick={() => fileRef.current?.click()} className="w-full flex flex-col items-center gap-2 border-2 border-dashed border-saddle/30 py-8 text-charcoal/60 hover:border-blaze hover:text-blaze transition-colors" data-testid="photo-pick-btn">
                  <ImagePlus size={26} />
                  <span className="text-sm font-semibold">Add a photo of your plate</span>
                </button>
              ) : (
                <div>
                  <img src={preview} alt="Preview" className="w-full max-h-72 object-cover" data-testid="photo-preview" />
                  <input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="mt-3 w-full border border-saddle/25 px-3 py-2 text-sm text-charcoal focus:outline-none focus:border-blaze"
                    data-testid="photo-caption-input"
                  />
                  <div className="flex gap-3 mt-3">
                    <button onClick={submit} disabled={uploading} className="inline-flex items-center gap-2 bg-forest text-bone px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-forest/90 transition-colors disabled:opacity-60" data-testid="photo-submit-btn">
                      {uploading && <Loader2 size={15} className="animate-spin" />} Post photo
                    </button>
                    <button onClick={() => { setPreview(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-sm text-saddle hover:text-forest" data-testid="photo-cancel-btn">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-saddle/20 p-6 text-center" data-testid="photo-signin-cta">
              <p className="text-sm text-charcoal/70">Sign in to share a photo of your finished plate.</p>
              <button onClick={() => openAuth("login")} className="mt-3 inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="photo-signin-btn">Sign in to add a photo</button>
            </div>
          )}
        </div>

        {/* Gallery */}
        {photos.length > 0 ? (
          <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="recipe-photo-grid">
            {photos.map((p, i) => {
              const liked = isAuthed && (p.liked_by || []).includes(user?.id);
              return (
                <li key={p.id} className="bg-white border border-saddle/15 overflow-hidden" data-testid={`recipe-photo-${i}`}>
                  <img src={p.photo} alt={p.caption || "Finished plate"} className="w-full h-44 object-cover" />
                  <div className="p-3">
                    {p.caption && <p className="text-sm text-charcoal/80">{p.caption}</p>}
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-charcoal/50">{p.author_name}</span>
                      <button onClick={() => toggleLike(p.id)} className="inline-flex items-center gap-1 text-xs text-charcoal/60 hover:text-blaze transition-colors" data-testid={`photo-like-${i}`}>
                        <Heart size={14} className={liked ? "fill-blaze text-blaze" : ""} /> {(p.liked_by || []).length}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-8 text-sm text-charcoal/50" data-testid="recipe-photos-empty">No finished plates yet — be the first to share one.</p>
        )}
      </div>
    </section>
  );
}
